import type { Font, FontCollection } from "fontkit";

export interface FontCoverage {
  test: (text: string) => string[];
  fontName?: string;
}

const coverageCache = new Map<string, Promise<FontCoverage>>();

const supportsEverything: FontCoverage = { test: () => [] };

const isFontCollection = (
  parsed: Font | FontCollection
): parsed is FontCollection => !("hasGlyphForCodePoint" in parsed);

// downloads a font, parses it, and returns a coverage object
const buildCoverage = async (fontUrl: string): Promise<FontCoverage> => {
  const response = await fetch(fontUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch font (HTTP ${response.status})`);
  }
  const buffer = await response.arrayBuffer();
  const { create } = await import("fontkit");
  const parsed = create(new Uint8Array(buffer) as unknown as Buffer);
  const font: Font | undefined = isFontCollection(parsed)
    ? parsed.fonts[0]
    : parsed;
  if (!font) {
    throw new Error("Font file contains no usable face");
  }

  return {
    fontName: font.familyName || font.fullName || undefined,
    test: (text: string): string[] => {
      const missing = new Set<string>();
      for (const char of text) {
        if (/\s/.test(char)) continue;
        const codePoint = char.codePointAt(0);
        if (codePoint === undefined) continue;
        if (!font.hasGlyphForCodePoint(codePoint)) {
          missing.add(char);
        }
      }
      return [...missing];
    },
  };
};

export const getFontCoverage = (
  fontUrl?: string | null
): Promise<FontCoverage> => {
  if (!fontUrl) return Promise.resolve(supportsEverything);

  let coverage = coverageCache.get(fontUrl);
  if (!coverage) {
    coverage = buildCoverage(fontUrl).catch((error) => {
      console.warn(
        `[fontGlyphCoverage] Could not analyze font: ${fontUrl}`,
        error
      );
      return supportsEverything;
    });
    coverageCache.set(fontUrl, coverage);
  }
  return coverage;
};

/** Clears the in-memory coverage cache. Intended for tests. */
export const clearFontCoverageCache = (): void => {
  coverageCache.clear();
};
